/**
 * API Error Handler Module
 *
 * Centralized error handling for API responses.
 */

/**
 * Custom error class that preserves HTTP status codes.
 *
 * @example
 * throw new ApiError("Resource not found", 404);
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Default error message when backend doesn't provide one.
 */
const DEFAULT_ERROR_MESSAGE = "An error occurred. Please try again.";

/**
 * Format API error detail into a user-friendly message.
 * Trusts the backend to provide sanitized messages.
 *
 * @param status - The HTTP status code from the response
 * @param detail - The error detail from the API response body
 * @returns A user-friendly error message
 */
export function formatApiError(status: number, detail: unknown): string {
  // Use string messages directly from backend
  if (typeof detail === "string" && detail.length > 0) {
    return detail;
  }

  // Handle validation errors array - join messages
  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail
      .slice(0, 3)
      .map((err) => {
        if (typeof err === "string") return err;
        if (err && typeof err.msg === "string") return err.msg;
        return null;
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(". ");
    }
  }

  // Handle object with message property
  if (detail && typeof detail === "object" && "message" in detail) {
    const msg = (detail as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) {
      return msg;
    }
  }

  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Handle an API error response and throw an appropriate ApiError.
 *
 * @param response - The fetch Response object
 * @throws ApiError with message and status code
 */
export async function handleApiError(response: Response): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  const errorMessage = formatApiError(response.status, errorData.detail);

  if (import.meta.env.DEV) {
    console.error("API Error:", { status: response.status, detail: errorData.detail });
  }

  throw new ApiError(errorMessage, response.status);
}
